import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { allSettled, dropTask, restartableTask, timeout } from 'ember-concurrency';
import { DASHBOARD_MAX_ANIMATION_DURATION_MS, DASHBOARD_TYPES, SPACER_IMAGE_URL } from 'sh-www/constants/dashboard';
import { DATA_ATTRIBUTES } from 'sh-www/constants/data-attributes';
import { BUYER_ACTION_TYPES } from 'sh-www/models/user-action-item';

const CLASS_NAME = 'SH-r-dashboard';

const DASHBOARD_COMPANY_DEFAULT_INCLUDES = [
  'company-membership-stats',
  'company-posting-stats',
  'company-project-stats',
  'statistics',
];

const ACTION_TYPE_TO_DATA_REF_HASH = {
  [BUYER_ACTION_TYPES.OVERDUE_INVOICE]: DATA_ATTRIBUTES.REF.BUYER_DASHBOARD_ACTION_OVERDUE_INVOICE_CARD,
  [BUYER_ACTION_TYPES.APPLICATION_AUTOCOMPLETING]: DATA_ATTRIBUTES.REF.BUYER_DASHBOARD_ACTION_PROJECT_AUTOCOMPLETE_CARD,
  [BUYER_ACTION_TYPES.POSTING_APPLICATIONS_EXPIRING]: DATA_ATTRIBUTES.REF.BUYER_DASHBOARD_ACTION_APPLICATIONS_EXPIRING_CARD,
  [BUYER_ACTION_TYPES.APPLICATION_PENDING_COMPLETION]: DATA_ATTRIBUTES.REF.BUYER_DASHBOARD_ACTION_PROJECT_AWAITING_COMPLETION_CARD,
  [BUYER_ACTION_TYPES.ADVANCE_REQUEST]: DATA_ATTRIBUTES.REF.BUYER_DASHBOARD_ACTION_ADVANCE_REQUEST_CARD,
  [BUYER_ACTION_TYPES.POSTING_DEADLINE]: DATA_ATTRIBUTES.REF.BUYER_DASHBOARD_ACTION_APPLICATION_DEADLINE_CARD,
  [BUYER_ACTION_TYPES.INVOICE_DUE]: DATA_ATTRIBUTES.REF.BUYER_DASHBOARD_ACTION_INVOICES_APPROACHING_OVERDUE_CARD,
  [BUYER_ACTION_TYPES.COUNTER_OFFER]: DATA_ATTRIBUTES.REF.BUYER_DASHBOARD_ACTION_REVIEW_COUNTER_OFFERS_CARD,
  [BUYER_ACTION_TYPES.POSTING_APPLICATION_REVIEW]: DATA_ATTRIBUTES.REF.BUYER_DASHBOARD_ACTION_APPLICATIONS_PITCHES_TO_REVIEW_CARD,
};

const TIER_INVOICE_VIEW_PERMISSIONS = new Set([
  3, // Professional
  4, // Enterprise
  6, // Business
]);

const TIER_SUBSCRIBE_VIEW_PERMISSIONS = new Set([
  0, // DIY
  1, // Pay As You Go
  2, // Pro Trial
  5, // Create
]);

const TIER_BLOG_VIEW_PERMISSIONS = new Set([
  3, // Professional
  4, // Enterprise
  6, // Business
]);

class BuyerDashboardController extends Controller {
  @controller application;

  @service('commands/user-commands') userCommandService;
  @service('commands/support-commands') supportCommandService;
  @service api;
  @service errorTracker;
  @service ghost;
  @service media;
  @service moment;
  @service router;
  @service session;
  @service store;

  actionItems = [];
  company = null;
  latestBlogPost = null;
  latestSupportArticle = null;

  get className() {
    return CLASS_NAME;
  }

  get dashboardType() {
    return DASHBOARD_TYPES.BUYER;
  }

  get currentUserRoleText() {
    return this.session.currentUser.isCompanyAdmin ? 'Admin' : 'Member';
  }

  get shouldShowSubscribe() {
    return this.session.currentUser.company.tier < 2;
  }

  get shouldShowLearn() {
    const { currentUser } = this.session;
    return currentUser.isCompanyEditor
      && !currentUser.isCompanyAdmin;
  }

  get shouldShowReports() {
    const { currentUser } = this.session;
    return currentUser.isCompanyEditor
      && currentUser.isCompanyAdmin;
  }

  get shouldShowInvoices() {
    const { currentUser } = this.session;
    return currentUser.isCompanyEditor
      && currentUser.isCompanyAdmin
      && TIER_INVOICE_VIEW_PERMISSIONS.has(currentUser.company.tier);
  }

  get shouldShowBlog() {
    const { currentUser } = this.session;
    return currentUser.isCompanyEditor
      && !currentUser.isCompanyAdmin
      && TIER_BLOG_VIEW_PERMISSIONS.has(currentUser.company.tier);
  }

  get shouldShowSubscribe() {
    return TIER_SUBSCRIBE_VIEW_PERMISSIONS.has(this.session.currentUser.company.tier);
  }

  get spacerImageUrl() {
    return SPACER_IMAGE_URL;
  }

  @action
  mapActionTypeToDataRef(actionType) {
    return ACTION_TYPE_TO_DATA_REF_HASH[actionType];
  };

  @action
  handleProfileLinkClick(profileOverlay) {
    this.set('application.profileOverlay', profileOverlay);
  }

  @action
  handleProfileOverlayClose() {
    this.set('application.profileOverlay', null);
  }

  @restartableTask
  *bootstrapPage() {
    yield Promise.race([
      this.loadModuleData.perform(),
      timeout(DASHBOARD_MAX_ANIMATION_DURATION_MS),
    ]);
  }

  @restartableTask
  *loadModuleData() {
    const { currentUser } = this.session;

    const companyIncludes = DASHBOARD_COMPANY_DEFAULT_INCLUDES;
    if (this.shouldShowInvoices) {
      companyIncludes.push('company-invoice-stats');
    }
    if (this.shouldShowReports) {
      companyIncludes.push('company-latest-report-stats');
    }

    const modulePromises = [
      this.getActionItems.perform(currentUser.id),
      this.getCompany.perform(currentUser.company.id, companyIncludes),
    ];

    if (this.shouldShowBlog) {
      modulePromises.push(this.getLatestBlogPost.perform(currentUser.company.id));
    }

    if (this.shouldShowLearn) {
      modulePromises.push(this.getLatestSupportArticle.perform());
    }

    yield allSettled(modulePromises);
  }

  @dropTask
  *getActionItems(userId) {
    try {
      const actionItems = yield this.userCommandService.getUserActionItems(userId);
      this.set('actionItems', actionItems);
    } catch (error) {
      this.errorTracker.error('getActionItems -- Could not fetch user action items', this);
    }
  }

  @dropTask
  *getCompany(companyId, includes) {
    const params = {
      include: includes.join(','),
    };

    const response = yield this.api.request(`/companies/${companyId}`, { params });

    const store = this.store;
    store.pushPayload(response);
    const company = store.peekRecord('company', response.data.id);

    this.set('company', company);
  }

  @dropTask
  *getLatestBlogPost() {
    try {
      const response = yield this.ghost.api.posts.browse({
        limit: 1,
      });

      if (response?.[0]) {
        const blog = response[0];
        const record = this.store.createRecord('ghost-blog', {
          excerpt: blog['excerpt'],
          featureImage: blog['feature_image'],
          featureImageCaption: blog['feature_image_caption'],
          publishedAt: blog['published_at'],
          readingTime: blog['reading_time'],
          slug: blog['slug'],
          title: blog['title'],
          url: blog['url'],
        });

        this.set('latestBlogPost', record);
      }
    } catch(e) {
      this.errorTracker.error('getLatestBlogPost -- Could not fetch latest Ghost blog', this);
    }
  }

  @dropTask
  *getLatestSupportArticle() {
    this.set('latestSupportArticle', {
      body: '',
      htmlUrl: 'https://help.glimmer.io/en/articles/8392550-your-creator-network-and-network-size',
      name: 'Your Creator Network and Network Size',
      title: 'Your Creator Network and Network Size',
      categoryName: 'For Brands',
      sectionName: 'Creator Management',
    });

    // try {
    //   const options = {
    //     params: {
    //       category: 'client',
    //       per_page: 1,
    //       sideloads: ['categories', 'sections'],
    //     },
    //   };
    //
    //   const response = yield this.supportCommandService.loadHelpCenterArticles(options);
    //   const [article] = response.articles;
    //   const [category] = response.categories;
    //   const [section] = response.sections;
    //
    //   this.set('latestSupportArticle', {
    //     body: article['body'],
    //     htmlUrl: article['html_url'],
    //     name: article['name'],
    //     title: article['title'],
    //     categoryName: category.name,
    //     sectionName: section.name,
    //   });
    // } catch(e) {
    //   this.errorTracker.error('getLatestSupportArticle -- Could not fetch latest support article', this);
    // }
  }
}

export default BuyerDashboardController;
